// src/admin/AddProductForm.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { API_BASE } from "../utils/api";

export default function AddProductForm({
  closeForm,
  onSuccess,
  productToEdit,
}) {
  const isEditMode = !!productToEdit;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    company: "Friends",
    type: "Mix",
    size: "",
    length: "",
    parity: "",
    botStock: "",
    standardName: "",
    rate: "",
    img: "",
  });

  // THIS IS THE KEY: Properly fill form when editing
  useEffect(() => {
    if (isEditMode && productToEdit) {
      setFormData({
        company: productToEdit.company || "Friends",
        type: productToEdit.type || "Mix",
        size: productToEdit.size?.trim() || "",
        length: productToEdit.length?.trim() || "",
        parity: productToEdit.parity?.trim() || "",
        botStock: productToEdit.botStock || 0,
        standardName: productToEdit.standardName?.trim() || "",
        rate: productToEdit.rate || 0,
        img: productToEdit.img || "",
      });
    } else if (!isEditMode) {
      // Reset to defaults when adding new
      setFormData({
        company: "Friends",
        type: "Mix",
        size: "",
        length: "",
        parity: "",
        botStock: "",
        standardName: "",
        rate: "",
        img: "",
      });
    }
  }, [productToEdit, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, img: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, img: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.company) return setError("Company is required");
    if (!formData.standardName.trim())
      return setError("Standard Name is required");
    if (!formData.size.trim()) return setError("Size is required");
    if (!formData.type) return setError("Type is required");
    if (!formData.rate || formData.rate <= 0)
      return setError("Valid Rate is required");
    if (!formData.botStock || formData.botStock < 0)
      return setError("Valid Stock is required");
    if (!formData.length.trim()) return setError("Length is required");
    if (!formData.parity.trim()) return setError("Parity/Extra is required");
    if (!formData.img) return setError("Product Image is required");

    setLoading(true);

    try {
      const url = isEditMode
        ? `${API_BASE}/stocks/${productToEdit._id}`
        : `${API_BASE}/stocks/add-stock`;

      const method = isEditMode ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company: formData.company,
          standardName: formData.standardName.trim(),
          size: formData.size.trim(),
          type: formData.type,
          rate: Number(formData.rate),
          botStock: Number(formData.botStock),
          length: formData.length.trim(),
          parity: formData.parity.trim(),
          img: formData.img,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Operation failed");

      setSuccess(
        isEditMode
          ? "Product updated successfully!"
          : "Product added successfully!"
      );
      setTimeout(() => {
        closeForm();
        onSuccess?.();
      }, 1500);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          mx: "auto",
          borderRadius: 5,
          p: { xs: 4, sm: 6 },
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          fontFamily="'Arvo', serif"
          color="#1f2937"
          textAlign="center"
          mb={5}
        >
          {isEditMode ? "Edit Product" : "Add New Product"}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
          {/* COMPANY — NOW 100% FILLS CORRECTLY */}
          <FormControl fullWidth required>
            <InputLabel>Company Name</InputLabel>
            <Select
              name="company"
              value={formData.company}
              onChange={handleChange}
              label="Company Name *"
            >
              <MenuItem value="Friends">Friends</MenuItem>
              <MenuItem value="Darpan">Darpan</MenuItem>
              <MenuItem value="Aashiana">Aashiana</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Standard Name"
            name="standardName"
            value={formData.standardName}
            onChange={handleChange}
            required
            fullWidth
            placeholder="e.g. ISA 50x50x6 Angle"
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
            }}
          >
            <TextField
              label="Size"
              name="size"
              value={formData.size}
              onChange={handleChange}
              required
              fullWidth
              placeholder="50x6, 16mm"
            />
            <FormControl fullWidth required>
              <InputLabel>Type</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={handleChange}
                label="Type *"
              >
                <MenuItem value="Mix">Mix</MenuItem>
                <MenuItem value="A">Angle (A)</MenuItem>
                <MenuItem value="F">Flat (F)</MenuItem>
                <MenuItem value="R">Round (R)</MenuItem>
                <MenuItem value="S">Square (S)</MenuItem>
                <MenuItem value="C">Channel (C)</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
            }}
          >
            <TextField
              label="Rate (₹/kg)"
              name="rate"
              type="number"
              value={formData.rate}
              onChange={handleChange}
              required
              fullWidth
              inputProps={{ step: "0.01" }}
            />
            <TextField
              label="Stock Quantity (Kgs)"
              name="botStock"
              type="number"
              value={formData.botStock}
              onChange={handleChange}
              required
              fullWidth
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
            }}
          >
            <TextField
              label="Length"
              name="length"
              value={formData.length}
              onChange={handleChange}
              required
              fullWidth
              placeholder="12 Meter, Random"
            />
            <TextField
              label="Parity / Extra"
              name="parity"
              value={formData.parity}
              onChange={handleChange}
              required
              fullWidth
              placeholder="+100, Ex-Plant"
            />
          </Box>

          {/* Image */}
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight="600"
              mb={2}
              color="#374151"
            >
              Product Image <span style={{ color: "#ef4444" }}>*</span>
            </Typography>

            {!formData.img ? (
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<CloudUploadIcon />}
                sx={{
                  py: 6,
                  borderStyle: "dashed",
                  borderWidth: 3,
                  borderRadius: 4,
                }}
              >
                Click to Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>
            ) : (
              <Box sx={{ position: "relative", width: "100%" }}>
                <img
                  src={formData.img}
                  alt="Preview"
                  style={{
                    width: "100%",
                    maxHeight: 400,
                    objectFit: "cover",
                    borderRadius: 24,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                  }}
                />
                <Button
                  onClick={removeImage}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    bgcolor: "rgba(239,68,68,0.9)",
                    color: "white",
                    borderRadius: "50%",
                    minWidth: 48,
                    height: 48,
                    "&:hover": { bgcolor: "#dc2626" },
                  }}
                >
                  <CloseIcon />
                </Button>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  Change Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 3, mt: 6 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            fullWidth
            size="large"
            sx={{
              bgcolor: "#facc15",
              color: "#000",
              fontWeight: "bold",
              py: 2.5,
              borderRadius: 4,
              "&:hover": { bgcolor: "#f59e0b" },
            }}
          >
            {loading ? (
              <CircularProgress size={32} />
            ) : isEditMode ? (
              "Update Product"
            ) : (
              "Save Product"
            )}
          </Button>
          <Button
            variant="outlined"
            onClick={closeForm}
            fullWidth
            size="large"
            sx={{ py: 2.5, borderRadius: 4, fontWeight: "bold" }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
